<script setup>
import { computed } from 'vue';
import { CATEGORIES } from '../utils/taskMeta';

const props = defineProps({
  campaign: { type: Object, default: null },
});

const statusByCategory = computed(() => {
  if (!props.campaign) return [];
  const counts = props.campaign.taskCountsByCategory || {};
  return CATEGORIES.map(c => ({ ...c, total: counts[c.value] || 0 }));
});

const totalTasks = computed(() => {
  const s = props.campaign?.taskCountsByStatus || {};
  return Object.values(s).reduce((a, b) => a + b, 0);
});

const done = computed(() => props.campaign?.taskCountsByStatus?.concluida || 0);
const pct = computed(() => (totalTasks.value ? Math.round((done.value / totalTasks.value) * 100) : 0));

const daysLabel = computed(() => {
  const d = props.campaign?.daysUntilElection;
  if (d === null || d === undefined) return '—';
  if (d < 0) return `há ${Math.abs(d)} dias`;
  if (d === 0) return 'hoje!';
  return `${d} dias`;
});
</script>

<template>
  <div v-if="campaign" class="space-y-4">
    <!-- Top row: identity -->
    <div class="grid grid-cols-1 gap-3 lg:grid-cols-4">
      <div class="rounded-xl border border-n-strong/40 bg-gradient-to-br from-blue-600 to-indigo-700 p-4 text-white">
        <p class="text-[10px] font-semibold uppercase opacity-80">Candidato</p>
        <p class="mt-1 text-lg font-bold">{{ campaign.candidateName || '—' }}</p>
        <p class="text-xs opacity-90">
          {{ campaign.office || 'Cargo não definido' }}{{ campaign.party ? ` · ${campaign.party}` : '' }}
        </p>
      </div>

      <div class="rounded-xl border border-n-strong/40 bg-n-background p-4">
        <p class="text-[10px] font-semibold uppercase text-n-slate-11">Número</p>
        <p class="mt-1 font-mono text-3xl font-black text-n-slate-12">
          {{ campaign.candidateNumber || '--' }}
        </p>
      </div>

      <div class="rounded-xl border border-n-strong/40 bg-n-background p-4">
        <p class="text-[10px] font-semibold uppercase text-n-slate-11">Até a eleição</p>
        <p class="mt-1 text-2xl font-bold text-n-slate-12">{{ daysLabel }}</p>
        <p class="text-[10px] text-n-slate-11">
          {{ campaign.electionDate ? new Date(campaign.electionDate).toLocaleDateString('pt-BR') : 'Defina a data' }}
        </p>
      </div>

      <div class="rounded-xl border border-n-strong/40 bg-n-background p-4">
        <p class="text-[10px] font-semibold uppercase text-n-slate-11">Meta de votos</p>
        <p class="mt-1 text-2xl font-bold text-n-slate-12">
          {{ campaign.targetVotes ? campaign.targetVotes.toLocaleString('pt-BR') : '—' }}
        </p>
      </div>
    </div>

    <!-- Progress -->
    <div class="rounded-xl border border-n-strong/40 bg-n-background p-4">
      <div class="mb-2 flex items-center justify-between">
        <p class="text-sm font-semibold text-n-slate-12">Progresso geral de tarefas</p>
        <p class="text-xs text-n-slate-11">
          {{ done }} / {{ totalTasks }} concluídas · {{ pct }}%
        </p>
      </div>
      <div class="h-2.5 w-full overflow-hidden rounded-full bg-n-slate-2">
        <div class="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all" :style="`width: ${pct}%`" />
      </div>
    </div>

    <!-- By category -->
    <div class="grid grid-cols-2 gap-3 md:grid-cols-5">
      <div
        v-for="c in statusByCategory"
        :key="c.value"
        class="rounded-xl border border-n-strong/40 bg-n-background p-3"
      >
        <div class="flex items-center gap-2">
          <span :class="[c.color, 'flex size-7 items-center justify-center rounded-md']">
            <span :class="c.icon" class="size-3.5" />
          </span>
          <p class="text-[11px] font-semibold text-n-slate-12">{{ c.label }}</p>
        </div>
        <p class="mt-2 text-xl font-bold text-n-slate-12">{{ c.total }}</p>
        <p class="text-[10px] text-n-slate-11">tarefas</p>
      </div>
    </div>
  </div>
</template>
